/**
 * Shared helpers for shopify--color-pattern / window.settings.color_swatches.
 * Liquid seeds the first ~250 entries; this module can fetch the rest via Storefront API.
 */
(function (global) {
  const COLOR_PATTERN_TYPE = 'shopify--color-pattern';
  const PAGE_SIZE = 250;

  const FACET_COLOR_FALLBACKS = {
    beige: '#f5f5dc',
    black: '#000000',
    blue: '#2563eb',
    brown: '#8b4513',
    gold: '#d4af37',
    green: '#228b22',
    grey: '#808080',
    gray: '#808080',
    ivory: '#fffff0',
    multi: 'conic-gradient(#e11d48, #f59e0b, #22c55e, #3b82f6, #a855f7, #e11d48)',
    navy: '#1b2452',
    nude: '#e8c4a8',
    orange: '#ff8c00',
    pink: '#ff69b4',
    purple: '#7c3aed',
    red: '#dc2626',
    silver: '#c0c0c0',
    white: '#ffffff',
    yellow: '#facc15',
  };

  let hydratePromise = null;
  let hydrated = false;

  const parseColorSwatchesCsv = (raw) => {
    if (!raw || typeof raw !== 'string') return [];
    return raw
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((entry) => {
        const idx = entry.indexOf(':');
        if (idx === -1) return { key: entry.toLowerCase(), value: '' };
        return {
          key: entry.slice(0, idx).trim().toLowerCase(),
          value: entry.slice(idx + 1).trim(),
        };
      });
  };

  const ensureMap = () => {
    if (!global.NatoriColorSwatches) global.NatoriColorSwatches = {};
    if (!global.NatoriColorSwatches.map) global.NatoriColorSwatches.map = {};
    if (!Array.isArray(global.NatoriColorSwatches.rows)) global.NatoriColorSwatches.rows = [];
    return global.NatoriColorSwatches.map;
  };

  const upsertRow = (row) => {
    if (!row?.key) return false;
    const key = String(row.key).trim();
    const keyLower = key.toLowerCase();
    if (!keyLower) return false;

    const map = ensureMap();
    if (map[keyLower]) return false;

    // Base facet labels are owned by FACET_COLOR_FALLBACKS (see seedFacetFallbacks).
    // Skip catalog metaobjects named exactly "Blue", "Multi", etc.
    if (FACET_COLOR_FALLBACKS[keyLower]) return false;

    const val = row.color || row.val || row.image || '';
    map[keyLower] = {
      key,
      val,
      color: row.color || '',
      image: row.image || '',
    };
    global.NatoriColorSwatches.rows.push(map[keyLower]);

    if (val) {
      const csv = global.settings?.color_swatches || '';
      const piece = `${key}:${val}`;
      global.settings = global.settings || {};
      global.settings.color_swatches = csv ? `${csv},${piece}` : piece;
      global.settings.color_swatches_map = map;
    }
    return true;
  };

  const getColorSwatchLookup = () => {
    const map = ensureMap();
    if (Object.keys(map).length) {
      const keyArray = Object.keys(map);
      const valArray = keyArray.map(
        (k) => map[k].color || map[k].val || map[k].image || ''
      );
      return { keyArray, valArray, map };
    }

    const rows = parseColorSwatchesCsv(global.settings?.color_swatches || '');
    rows.forEach((row) => {
      if (row.key && !map[row.key]) {
        map[row.key] = { key: row.key, val: row.value, color: row.value, image: '' };
      }
    });
    return {
      keyArray: Object.keys(map),
      valArray: Object.keys(map).map((k) => map[k].color || map[k].val || ''),
      map,
    };
  };

  const isSwatchImageValue = (value) => {
    if (!value || typeof value !== 'string') return false;
    if (/^(https?:)?\/\//i.test(value) || value.startsWith('/')) return true;
    if (value.includes('/cdn/') || value.includes('/files/')) return true;
    if (value.includes('gradient(')) return true;
    return value.includes('.') && !value.trim().startsWith('#');
  };

  const swatchBackgroundImageCss = (bgValue, shopDomain) => {
    if (!bgValue) return '';
    if (String(bgValue).includes('gradient(')) {
      return `--option-color-image: ${bgValue};`;
    }
    if (/^(https?:)?\/\//i.test(bgValue) || bgValue.startsWith('/') || bgValue.includes('/cdn/')) {
      return `--option-color-image: url('${bgValue}');`;
    }
    const domain = shopDomain || global.Shopify?.shop || global.location?.hostname || '';
    return `--option-color-image: url('//${domain}/cdn/shop/files/${bgValue}');`;
  };

  const isCssColorName = (name) => {
    if (!name) return false;
    try {
      if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
        return CSS.supports('color', name);
      }
    } catch (e) {
      // ignore
    }
    return Boolean(FACET_COLOR_FALLBACKS[String(name).toLowerCase()]);
  };

  const resolveFill = (colorName) => {
    const cleaned = String(colorName || '')
      .toLowerCase()
      .trim();
    if (!cleaned) return null;

    // Base facet labels (Blue, Multi, …) must stay stable. Hydration otherwise
    // picks the first shopify--color-pattern with that exact label, which is
    // often a product-specific pattern image or a bad solid.
    if (FACET_COLOR_FALLBACKS[cleaned]) {
      return FACET_COLOR_FALLBACKS[cleaned];
    }

    const { map } = getColorSwatchLookup();
    const mapped = map[cleaned];
    if (mapped) {
      // Prefer solid color over pattern image when both exist.
      return mapped.color || mapped.val || mapped.image || null;
    }

    if (isCssColorName(cleaned)) {
      return cleaned;
    }

    return null;
  };

  /** Lock simple facet colors into the map (overwrites any Liquid/catalog claim). */
  const seedFacetFallbacks = () => {
    const map = ensureMap();
    Object.keys(FACET_COLOR_FALLBACKS).forEach((key) => {
      const fill = FACET_COLOR_FALLBACKS[key];
      const isGradient = String(fill).includes('gradient(');
      map[key] = {
        key,
        val: fill,
        color: isGradient ? '' : fill,
        image: isGradient ? fill : '',
      };
    });
    if (global.settings) {
      global.settings.color_swatches_map = map;
    }
  };

  const resolveSwatchStyle = (colorName, shopDomain) => {
    let colorValue = String(colorName || '')
      .toLowerCase()
      .trim();
    const fill = resolveFill(colorValue);
    if (fill) colorValue = fill;

    if (isSwatchImageValue(colorValue) || String(colorValue).includes('gradient(')) {
      return {
        colorValue: 'var(--bg-body)',
        bgValue: colorValue,
        bgImageUrl: swatchBackgroundImageCss(colorValue, shopDomain),
      };
    }

    return {
      colorValue,
      bgValue: false,
      bgImageUrl: '',
    };
  };

  const parseMetaobjectNode = (node) => {
    if (!node) return null;
    const fields = Array.isArray(node.fields) ? node.fields : [];
    const byKey = {};
    fields.forEach((field) => {
      if (field?.key) byKey[field.key] = field;
    });

    const label =
      byKey.label?.value ||
      node.handle?.replace(/-/g, ' ') ||
      '';
    const color = byKey.color?.value || '';
    let image = '';
    const imageRef = byKey.image?.reference;
    if (imageRef?.image?.url) {
      image = imageRef.image.url;
    } else if (byKey.image?.value && String(byKey.image.value).includes('http')) {
      image = byKey.image.value;
    }

    if (!label || (!color && !image)) return null;
    return {
      key: label,
      // Prefer hex/rgb when present; keep image for pattern-only entries.
      val: color || image,
      color,
      image,
    };
  };

  const fetchColorPatternPage = async (token, shop, cursor) => {
    const query = `
      query ColorPatterns($type: String!, $first: Int!, $after: String) {
        metaobjects(type: $type, first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            handle
            fields {
              key
              value
              reference {
                ... on MediaImage {
                  image {
                    url
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(`https://${shop}/api/2024-10/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query,
        variables: {
          type: COLOR_PATTERN_TYPE,
          first: PAGE_SIZE,
          after: cursor || null,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Color pattern fetch failed: ${response.status}`);
    }

    const json = await response.json();
    if (json.errors?.length) {
      throw new Error(json.errors.map((e) => e.message).join('; '));
    }
    return json.data?.metaobjects || null;
  };

  const hydrateFromStorefront = async () => {
    if (hydrated) return ensureMap();
    if (hydratePromise) return hydratePromise;

    hydratePromise = (async () => {
      try {
        const token =
          global.storefrontAccessToken ||
          (typeof global.waitForStorefrontToken === 'function'
            ? await global.waitForStorefrontToken(2500).catch(() => null)
            : null);
        const shop = global.Shopify?.shop || global.location?.hostname;
        if (!token || !shop) {
          return ensureMap();
        }

        let cursor = null;
        let page = 0;
        // Liquid already seeded page 1 (~250). Keep fetching until exhausted.
        do {
          const connection = await fetchColorPatternPage(token, shop, cursor);
          if (!connection?.nodes?.length) break;

          connection.nodes.forEach((node) => {
            const row = parseMetaobjectNode(node);
            if (row) upsertRow(row);
          });

          cursor = connection.pageInfo?.hasNextPage ? connection.pageInfo.endCursor : null;
          page += 1;
        } while (cursor && page < 20);

        hydrated = true;
        global.dispatchEvent?.(new CustomEvent('natori:color-swatches-hydrated', {
          detail: { count: Object.keys(ensureMap()).length },
        }));
      } catch (error) {
        console.warn('NatoriColorSwatches hydrate failed', error);
      }
      return ensureMap();
    })();

    return hydratePromise;
  };

  // Seed base facet colors immediately, then hydrate the rest once interactive.
  seedFacetFallbacks();
  if (typeof document !== 'undefined') {
    const start = () => {
      seedFacetFallbacks();
      hydrateFromStorefront();
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  }

  global.NatoriColorSwatches = Object.assign(global.NatoriColorSwatches || {}, {
    parseColorSwatchesCsv,
    getColorSwatchLookup,
    isSwatchImageValue,
    swatchBackgroundImageCss,
    resolveSwatchStyle,
    resolveFill,
    isCssColorName,
    seedFacetFallbacks,
    hydrateFromStorefront,
    FACET_COLOR_FALLBACKS,
  });
})(typeof window !== 'undefined' ? window : globalThis);
